import { useCallback, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { getBookingsForDate, listTables } from '../../lib/api';
import { todayLocal } from '../../lib/time';
import { DatePicker } from '../../components/DatePicker';
import { BookingGrid } from './BookingGrid';
import { BookingDetailModal } from './BookingDetailModal';
import { PS, T } from '../../lib/theme';
import type { Booking } from '../../lib/types';

export function BookingsPage() {
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? todayLocal();

  const [selected, setSelected] = useState<Booking | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Stable loader closures keyed by `date`.
  const bookingsLoader = useCallback(
    (signal: AbortSignal) => getBookingsForDate(date, signal),
    [date],
  );
  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);

  const bookings = useApi<Booking[]>(bookingsLoader);
  const tables = useApi(tablesLoader);

  const onDateChange = (next: string) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('date', next);
      return p;
    });
  };

  const shiftDate = (days: number) => {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    onDateChange(d.toISOString().slice(0, 10));
  };

  const onSelect = (b: Booking) => {
    setSelected(b);
    setModalOpen(true);
  };

  // Tables come from the public /tables endpoint; filtering by is_active
  // is left as a follow-up once the admin Tables page exposes the flag.
  const visible = tables.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Bookings</h1>
        <button
          onClick={() => shiftDate(-1)}
          style={navBtnStyle}
          aria-label="Previous day"
        >
          ← Prev
        </button>
        <DatePicker value={date} onChange={onDateChange} ariaLabel="Booking date" />
        <button
          onClick={() => shiftDate(1)}
          style={navBtnStyle}
          aria-label="Next day"
        >
          Next →
        </button>
        <button
          onClick={() => onDateChange(todayLocal())}
          style={navBtnStyle}
        >
          Today
        </button>
        <Link
          to="/bookings/new"
          style={{
            background: PS.red,
            color: PS.white,
            padding: '8px 16px',
            borderRadius: 3,
            minHeight: 40,
            display: 'inline-flex',
            alignItems: 'center',
            ...T.buttonLabel,
            textTransform: 'uppercase' as const,
            textDecoration: 'none',
          }}
        >
          + New booking
        </Link>
      </header>

      {(bookings.loading || tables.loading) && (
        <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>
      )}

      {bookings.error && (
        <div
          style={{
            padding: 12,
            background: PS.errorBg,
            border: `1px solid ${PS.errorBorder}`,
            color: PS.errorText,
            borderRadius: 3,
          }}
        >
          {bookings.error.message}{' '}
          <button onClick={() => void bookings.refetch()} style={{ marginLeft: 8 }}>
            Retry
          </button>
        </div>
      )}

      {!bookings.loading && !tables.loading && visible.length > 0 && (
        <BookingGrid
          tables={visible}
          bookings={bookings.data ?? []}
          queryDate={date}
          isToday={date === todayLocal()}
          onSelect={onSelect}
        />
      )}

      <BookingDetailModal
        open={modalOpen}
        booking={selected}
        onClose={() => setModalOpen(false)}
        onChanged={() => void bookings.refetch()}
      />
    </div>
  );
}

const navBtnStyle: CSSProperties = {
  background: PS.white,
  border: `1px solid ${PS.border}`,
  padding: '8px 12px',
  borderRadius: 3,
  cursor: 'pointer',
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.13px',
  textTransform: 'uppercase' as const,
};
